// A missing number or address, added there and then (tidy-up item 8; Liam, 20 Oct 2026: "ship it all"; the drawing
// https://claude.ai/artifact/HZ9kZtcaAaGUEVbpuNivBo, docs/plans/tidy-up/AGREED.md section 8 and build-today.md). The
// Diary's visit (+ Add a number, + Add an address, + Add details where Message would be) and Messages' notice after
// placing someone call openAddDetails; missingDetails says what is missing and the button's words.
//
// One box with only what is missing: the number first (the message has to go before the day), then the address (needed
// on the day). One save on the customer (PATCH /api/customers/:id with `base`, so a change to another box made on
// another phone does not stop it), with Undo. Never for a worker: they cannot change a customer.
//
// Or nothing to add (tidy-up 2 item 3; Liam, 20 Oct 2026: "I cant add a number to The phothray studio as its a business
// so will it stay there forever?", then "do it" on https://claude.ai/artifact/QjKSqEL4tJpq2uPeLb4CA1;
// docs/plans/tidy-up-2/AGREED.md section 3, build-messages.md): "They don't take messages" sets their way to Don't
// message (customers.message_by 'none', 0036), "The postcode is enough" stops the ask for a street
// (customers.postcode_enough, 0079). Either is changed back on their page.
//
// A commercial customer, with Invoices on (Liam, 20 Oct 2026, "if we tag a job as commercial we can generate invoice";
// docs/plans/commercial/AGREED.md items 12 and 15; the shared rules are build-money's src/commercial.ts): the box asks for
// a mobile or an email for invoices until one an invoice can go to is on file (a mobile that takes texts, their email, or
// the invoice-to email), Don't message or not, and never again after; "They don't take messages" is not offered.
// Invoiced each visit or Once a month alike: a month's invoice needs a way to reach them too (the lead, 20 Oct 2026).
// Being commercial adds that ask and never removes one: their own way still asks for what it needs (Sonnet's recheck S6,
// 21 Oct 2026). The box is "for invoices" (what is added never changes how they are messaged) only from the invoice step
// and for a customer set to Don't message; anyone else keeps today's box, "Save and message" and all (S7).

import { pinFoot } from './pin-foot'
import { api, dismiss, getQueue, mutate } from './api'
import { invoicesOn } from './business-settings'
import { invoiceReach, isCommercial } from './commercial'
import { customerMessageBy, EMAIL_RULE, linkFor, roundMessageBy, textsReach, wayFor, wayWords, type CustomerMessageBy } from './contact'
import { phonePlaceholder, sheet } from './customer-sheets'
import { firstName } from './late-message'
import { isWorker } from './me'
import { postcodeFromAddress } from './quote-message'
import { postcodeWords } from './round-config'
import { clear, el, splitAccessWarning, telHref, toast } from './ui'
import { cursorOnOpen } from './sheet-keyboard'
import { undoPatch } from './undo'
import './messages.css'

// The customer as the Diary's customers list holds them (every column), or as their page reads them.
export interface DetailsCustomer {
  id: number
  name: string
  version: number
  phone?: string | null
  email?: string | null
  message_by?: string | null
  address?: string | null
  postcode?: string | null
  postcode_enough?: number | null
  latitude?: number | null
  longitude?: number | null
  geocoded_postcode?: string | null
  // Commercial (0080), how often they are invoiced, and the invoice-to email an invoice can go to (0065).
  commercial?: number | boolean | null
  invoice_mode?: string | null
  invoice_to_email?: string | null
}

export interface MissingDetails {
  // No way reaches them by their way (Sonnet's edge cases, 20 Oct 2026, S E9): no number, a landline, "NO WA" on a
  // WhatsApp round, words with no number in them, or no email when email is their way. Never for "Don't message".
  // A commercial customer with Invoices on: also when nothing an invoice can go to (commercial.ts invoiceReach), whatever
  // their way (S6: their way's own ask stays).
  number: boolean
  // No street: blank, or only a postcode, or only an access warning ("DO NOT BOOK 11-1PM"). Not when the owner said the
  // postcode is enough and there is one (tidy-up 2 item 3).
  address: boolean
  // The box their way needs: Email when email is how they are messaged, else Mobile.
  field: 'phone' | 'email'
  // "+ Add a number", "+ Add an email", "+ Add an address", "+ Add details" (both), or null: nothing missing.
  words: string | null
}

// What is still waiting to send from this phone for a customer (no signal, or not answered yet), laid over what was
// read, in the order the changes were made, an Undo included; a refused one (409, or failed) counts for nothing (Astra's
// review F12, GPT-6.1's R11, 20 Oct 2026). So a read that lands while a choice waits never brings an ask back, and an
// Undo made with no signal brings it back at once. Messages' pop-up after placing reads the same (rebook.ts).
// The Commercial switch, how often they are invoiced and the invoice-to email too (commercial AGREED.md item 15).
const WAITING_FIELDS = ['phone', 'email', 'message_by', 'address', 'postcode', 'postcode_enough', 'commercial', 'invoice_mode', 'invoice_to_email'] as const
export function withWaiting<T extends { id: number }>(c: T): T {
  const path = `/api/customers/${c.id}`
  let out = c
  for (const q of getQueue()) {
    if (q.method !== 'PATCH' || q.path !== path || q.status === 'conflict' || q.status === 'failed' || !q.body || typeof q.body !== 'object') continue
    const body = q.body as Record<string, unknown>
    for (const k of WAITING_FIELDS) if (k in body) out = { ...out, [k]: body[k] }
  }
  return out
}

// Does a message reach them now, their way? (contact.ts linkFor: the send button has a link.)
export function reaches(c: Pick<DetailsCustomer, 'phone' | 'email' | 'message_by'>): boolean {
  const way = wayFor(c)
  return way !== 'none' && linkFor(way, c, ' ', ' ').href !== null
}


// Set to Don't message, with anything still waiting on this phone laid over (the Diary's option A: such a visit offers
// Mark confirmed, tidy-up 2 AGREED.md section 3).
export function takesNoMessages(c: { id: number; message_by?: string | null }): boolean {
  return customerMessageBy(withWaiting(c).message_by) === 'none'
}

// Invoices go to them: commercial (the switch still waiting on this phone included) on a round with Invoices on. The
// add box is for the owner only, so no role test here.
const forInvoices = (c: DetailsCustomer): boolean => invoicesOn() && isCommercial(c)

// The street in an address: without an access warning, and without a postcode at its end. An imported address that is
// only a postcode ("E17 9AA") has none (GPT-6.1's review of the build, 20 Oct 2026, E09).
function streetOf(address: string | null | undefined): string {
  const addr = splitAccessWarning(address ?? '').addr.trim()
  const postcode = addr ? postcodeFromAddress(addr) : null
  return postcode ? withoutPostcode(addr, postcode).trim() : addr
}

// The postcode at the end of an address as it is written there: a US ZIP+4 ("78701-2345") whole, though the ZIP found is
// its first five digits (Astra's review F17, GPT-6.1's R18, 20 Oct 2026).
function writtenPostcode(address: string): string | null {
  const found = postcodeFromAddress(address)
  if (!found) return null
  const plus4 = /^\d{5}$/.test(found) ? /(?:^|[\s,])(\d{5}-\d{4})\s*$/.exec(address) : null
  return plus4 && plus4[1]!.startsWith(found) ? plus4[1]! : found
}

// The postcode they have: the Postcode box, else the one an address with no street is (the same two the box puts in).
function postcodeOf(c: Pick<DetailsCustomer, 'address' | 'postcode'>): string | null {
  const own = (c.postcode ?? '').trim()
  if (own) return own
  const addr = splitAccessWarning(c.address ?? '').addr.trim()
  return addr && !streetOf(addr) ? writtenPostcode(addr) : null
}

// "The postcode is enough", and there is a postcode to be enough: taken away later, the street is asked for again.
const postcodeIsEnough = (c: Pick<DetailsCustomer, 'address' | 'postcode' | 'postcode_enough'>): boolean => Number(c.postcode_enough) === 1 && !!postcodeOf(c)

export function missingDetails(c: DetailsCustomer): MissingDetails {
  const w = withWaiting(c)
  const field = wayFor(w) === 'email' ? 'email' : 'phone'
  if (isWorker()) return { number: false, address: false, field, words: null }
  const number = (wayFor(w) !== 'none' && !reaches(w)) || (forInvoices(w) && !invoiceReach(w).any)
  const address = !streetOf(w.address) && !postcodeIsEnough(w)
  const words = number && address ? '+ Add details' : number ? (field === 'email' ? '+ Add an email' : '+ Add a number') : address ? '+ Add an address' : null
  return { number, address, field, words }
}

// The postcode the Diary's row shows instead of "No address yet" once the owner said it is enough; null otherwise.
export function enoughPostcode(c: DetailsCustomer): string | null {
  const w = withWaiting(c)
  return !streetOf(w.address) && postcodeIsEnough(w) ? postcodeOf(w) : null
}

// Their page's tick box under Address (build-messages.md 3.4): shown while they have no street and a postcode.
export function postcodeChoice(c: DetailsCustomer): { postcode: string; on: boolean } | null {
  const w = withWaiting(c)
  const postcode = streetOf(w.address) ? null : postcodeOf(w)
  return postcode ? { postcode, on: Number(w.postcode_enough) === 1 } : null
}

export interface DetailsSaved {
  // The customer with what was saved on it (the same object the caller passed in, updated): Message works with it at
  // once, and with no signal it carries the fields still waiting to send (GPT-6.1's edge cases, G E07).
  customer: DetailsCustomer
  // Kept on the phone, to send when there is signal.
  waiting: boolean
  // "Save and message" was pressed, and the saved details really reach them: open the message now. Never after "They
  // don't take messages" or "The postcode is enough" (Astra's review F18, GPT-6.1's R14, 20 Oct 2026).
  message: boolean
}

export interface AddDetailsOptions {
  // What to ask for; worked out from the customer (missingDetails) when left out.
  need?: { number?: boolean; address?: boolean }
  // The place offers a message (the visit's ask, the notice after placing): the button reads "Save and message <name>"
  // when what is typed would reach them.
  message?: boolean
  // For an invoice (the after-the-job invoice step, build-money): as for a commercial customer invoiced after each job.
  invoice?: boolean
  onSaved?: (r: DetailsSaved) => void
  onClosed?: () => void
}

const key = (v: unknown): string => String(v ?? '').replace(/\s+/g, '').toUpperCase()

// The address without the postcode at its end, as a booked enquiry's is stored (functions/lib/convert-lead.ts
// addressWithoutPostcode): a whole word only; the address itself when the postcode is not at its end.
function withoutPostcode(address: string, postcode: string): string {
  const want = key(postcode)
  if (!want) return address
  // A US ZIP+4 comes off whole, whichever of its two forms was found (Astra's review F17, GPT-6.1's R18).
  const zip = /^(\d{5})(?:-\d{4})?$/.exec(want)
  const atEnd = zip ? /(^|[\s,])(\d{5})(-\d{4})?\s*$/.exec(address) : null
  if (zip && atEnd && atEnd[2] === zip[1]) return address.slice(0, atEnd.index).replace(/[\s,]+$/, '')
  let seen = ''
  for (let i = address.length - 1; i >= 0; i--) {
    if (/\s/.test(address[i]!)) continue
    seen = address[i]!.toUpperCase() + seen
    if (seen.length === want.length) {
      if (seen !== want || (i > 0 && !/[\s,]/.test(address[i - 1]!))) return address
      return address.slice(0, i).replace(/[\s,]+$/, '')
    }
  }
  return address
}

// The same postcode however it is written: a US ZIP+4 is the same place as its ZIP.
const samePostcode = (a: string, b: string): boolean => key(postcodeFromAddress(a) ?? a) === key(postcodeFromAddress(b) ?? b)

// Why what is on file does not reach them, in the owner's words.
function onFileWords(c: DetailsCustomer, field: 'phone' | 'email'): string | null {
  const v = String(c[field] ?? '').trim()
  if (!v) return null
  if (field === 'email') return EMAIL_RULE.test(v) ? `On file: ${v}.` : `On file: ${v}, which isn't one email address.`
  if (!telHref(v)) return `On file: "${v}", which isn't a number.`
  if (wayFor(c) === 'whatsapp' && /no\s*(wa|whats\s*app)\b/i.test(v)) return `On file: ${v}, marked no WhatsApp.`
  if (!textsReach(v)) return `On file: ${v}. That's a landline: it can't take a text or WhatsApp.`
  return `On file: ${v}.`
}

const wayName = (way: CustomerMessageBy): string => wayWords(way).short

// Instead of typing: 'none' = They don't take messages, 'enough' = The postcode is enough.
type Choice = 'none' | 'enough'

type Plan =
  | { error: string; at: 'number' | 'address' | 'all' }
  | { choice: { typed: string; onFile: string } }
  | { set: Record<string, unknown>; before: Record<string, unknown>; base: Record<string, unknown>; result: DetailsCustomer; text: string }

// A box this save sends already holds what it would put there (after a refusal, read again).
function sameValue(k: string, a: unknown, b: unknown): boolean {
  if (k === 'postcode_enough') return Number(a ?? 0) === Number(b ?? 0)
  if (k === 'postcode' || k === 'phone' || k === 'email') return key(a) === key(b)
  return String(a ?? '').trim() === String(b ?? '').trim()
}

export function openAddDetails(c: DetailsCustomer, opts: AddDetailsOptions = {}): void {
  if (isWorker()) return
  const missing = missingDetails(c)
  const needNumber = opts.need?.number ?? missing.number
  const needAddress = opts.need?.address ?? missing.address
  // Nothing missing any more (added on another phone since the screen was drawn): straight on.
  if (!needNumber && !needAddress) { opts.onSaved?.({ customer: c, waiting: false, message: !!opts.message && reaches(c) }); return }
  const name = c.name.trim()
  const first = firstName(name) || 'them'
  // For an invoice: asked for by the invoice step, or a commercial customer with Invoices on who is set to Don't message,
  // anything still waiting on this phone included (items 12 and 15; S7: anyone messaged keeps today's box).
  const invoice = !!opts.invoice || (forInvoices(c) && wayFor(withWaiting(c)) === 'none')
  const s = sheet(name ? `${name}'s details` : 'Their details')
  s.dialog.classList.add('add-details-sheet')
  // Put away without saving (closed, or swiped down on a phone).
  let saved = false
  s.dialog.addEventListener('close', () => { if (!saved) opts.onClosed?.() })
  s.body.append(el('p', { class: 'muted field-help' }, 'Only what is missing. Saved on their page too.'))

  // ---- the number (or email) ----
  let field: 'phone' | 'email' = missing.field
  const typed: Record<'phone' | 'email', string> = { phone: '', email: '' }
  const numberBox = el('div', { class: 'add-details-number' })
  const numberNote = el('p', { class: 'muted field-help add-details-note', 'aria-live': 'polite' })
  const numberError = el('p', { class: 'form-error', role: 'alert' })
  let numberInput: HTMLInputElement | null = null
  const drawNumber = (): void => {
    clear(numberBox)
    const label = field === 'email' ? 'Email' : 'Mobile'
    const input = el('input', { type: field === 'email' ? 'email' : 'tel', autocomplete: 'off', 'aria-label': label, placeholder: field === 'email' ? 'name@example.com' : phonePlaceholder() }) as HTMLInputElement
    input.value = typed[field]
    input.addEventListener('input', () => { typed[field] = input.value; paint() })
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void go() } })
    numberInput = input
    const other = el('button', { type: 'button', class: 'link-button add-details-swap' }, field === 'email' ? 'add a mobile' : 'add an email')
    other.addEventListener('click', () => { field = field === 'email' ? 'phone' : 'email'; drawNumber(); numberInput?.focus(); paint() })
    const onFile = onFileWords(c, field)
    numberBox.append(el('label', { class: 'booking-field' }, el('span', {}, label), input))
    if (onFile) numberBox.append(el('p', { class: 'muted field-help add-details-on-file' }, `${onFile} What you put in here replaces it.`))
    numberBox.append(el('p', { class: 'muted field-help' }, invoice ? 'For invoices · or ' : 'To message them · or ', other), numberNote, numberError)
  }
  if (needNumber) { drawNumber(); s.body.append(numberBox) }

  // ---- the address ----
  const onFilePostcode = (c.postcode ?? '').trim()
  const warn = splitAccessWarning(c.address ?? '')
  const addressInput = el('input', { type: 'text', autocomplete: 'off', 'aria-label': 'Address', placeholder: postcodeWords('House number, street, postcode') }) as HTMLInputElement
  // The postcode they have is put in, ready for the street in front of it: the one on file, else the one an address
  // with no street holds (E09).
  const prefill = onFilePostcode || (streetOf(c.address) ? '' : warn.addr.trim())
  addressInput.value = prefill
  const addressError = el('p', { class: 'form-error', role: 'alert' })
  const choiceBox = el('div', { class: 'add-details-choice' })
  let postcodeChoice: 'typed' | 'on_file' | null = null
  let dropPostcode = false
  addressInput.addEventListener('input', () => { postcodeChoice = null; dropPostcode = false; clear(choiceBox); addressError.textContent = ''; paint() })
  if (needAddress) {
    s.body.append(el('label', { class: 'booking-field' }, el('span', {}, 'Address'), addressInput), el('p', { class: 'muted field-help' }, 'To find them on the day'))
    // An access warning stays with the street it is about (S E9 follow-up in AGREED.md).
    if (warn.warn) s.body.append(el('p', { class: 'muted field-help' }, `Kept with it: ${warn.warn}`))
    s.body.append(choiceBox, addressError)
  }

  const save = el('button', { type: 'button', class: 'btn-primary add-details-save' }, 'Save') as HTMLButtonElement
  s.body.append(s.error, pinFoot(save))
  const buttons: HTMLButtonElement[] = [save]

  // What saving now would do; with a choice, Save with that choice added.
  const plan = (choice?: Choice): Plan => {
    const set: Record<string, unknown> = {}
    const what: string[] = []
    let wayNow: CustomerMessageBy | null = null
    const value = needNumber ? typed[field].trim() : ''
    if (value) {
      if (field === 'email') {
        if (!EMAIL_RULE.test(value)) return { error: "That doesn't look like one email address.", at: 'number' }
        set.email = value
        // Adding only an email makes email their way, in the same save (AGREED.md section 8). Never for an invoice: how
        // a commercial customer is messaged stays as it is (commercial AGREED.md item 12, Astra C04, Sol C05, Sonnet H1).
        if (!invoice && wayFor(c) !== 'email') { set.message_by = 'email'; wayNow = 'email' }
        what.push('Email')
      } else {
        if (!telHref(value)) return { error: `Put in a phone number, like ${phonePlaceholder()}.`, at: 'number' }
        set.phone = value
        // A mobile for someone messaged by email: their way becomes the round's usual (or text), when the number takes it.
        // Not for an invoice (item 12).
        if (!invoice && wayFor(c) === 'email') {
          const round = roundMessageBy()
          const way = round === 'email' ? 'text' : round
          if (linkFor(way, { phone: value }, ' ', ' ').href) { set.message_by = way === round ? null : way; wayNow = way }
        }
        what.push('Number')
      }
    }
    const text = needAddress ? addressInput.value.trim() : ''
    // The postcode put in for them, left as it is, is not an address added.
    if (text && key(text) !== key(prefill)) {
      const found = postcodeFromAddress(text)
      let street = text
      let postcode: string | undefined
      if (found) {
        street = withoutPostcode(text, found)
        // As written: a US ZIP+4 is kept whole (F17, R18).
        const written = writtenPostcode(text) ?? found
        if (dropPostcode) postcode = undefined
        else if (!onFilePostcode) postcode = written
        else if (!samePostcode(found, onFilePostcode)) {
          // A different postcode from the one on file: asked plainly (today.ts:669 would otherwise show both).
          if (!postcodeChoice) return { choice: { typed: written, onFile: onFilePostcode } }
          if (postcodeChoice === 'typed') postcode = written
        }
      }
      if (street) {
        const keep = warn.warn ? (c.address ?? '').trim().slice(warn.addr.length).trim() : ''
        set.address = keep ? `${street}${keep.startsWith(',') ? '' : ' '}${keep}` : street
      }
      if (postcode !== undefined) set.postcode = postcode
      if (street || postcode !== undefined) what.push(onFilePostcode && postcode !== undefined ? 'Address changed' : 'Address')
    }
    // The choice wins over a way the typed number or email would have given them.
    if (choice === 'none') { set.message_by = 'none'; wayNow = null }
    if (choice === 'enough') set.postcode_enough = 1
    if (!Object.keys(set).length) {
      const ask = needNumber && needAddress ? 'Put in their number or their address.' : needNumber ? (field === 'email' ? 'Put in their email.' : 'Put in their number.') : 'Put in their address.'
      return { error: ask, at: 'all' }
    }
    const before: Record<string, unknown> = {}
    const base: Record<string, unknown> = {}
    for (const k of Object.keys(set)) {
      before[k] = (c as unknown as Record<string, unknown>)[k] ?? null
      if (k !== 'postcode') base[k] = before[k]
    }
    // A customer read before 0079 has no postcode_enough: it was 0.
    if ('postcode_enough' in set) { before.postcode_enough = Number(c.postcode_enough ?? 0); base.postcode_enough = before.postcode_enough }
    // Undo puts back the map position a new postcode clears (G E04): the server takes it only with the postcode.
    if ('postcode' in set && key(set.postcode) !== key(c.postcode)) {
      before.latitude = c.latitude ?? null
      before.longitude = c.longitude ?? null
      before.geocoded_postcode = c.geocoded_postcode ?? null
    }
    const result: DetailsCustomer = { ...c, ...set }
    const label = what.length > 1 ? 'Details added' : what[0] === 'Address changed' ? 'Address changed' : what.length ? `${what[0]} added` : ''
    const who = name || 'They'
    const parts = [
      label ? `${label} for ${name || 'them'}${wayNow ? `. ${wayName(wayNow)} is now how they're messaged` : ''}` : '',
      choice === 'none' ? `${who}: Don't message. Change it on their page` : '',
      choice === 'enough' ? `${who}: ${postcodeWords('the postcode is enough')}. Change it on their page` : '',
    ]
    return { set, before, base, result, text: parts.filter(Boolean).join('. ') }
  }

  // The button and the line under the number say what saving now would do.
  const paint = (): void => {
    numberError.textContent = ''
    s.error.textContent = ''
    const p = plan()
    const result = 'result' in p ? p.result : c
    const goes = !!opts.message && 'result' in p && reaches(result)
    save.textContent = goes ? `Save and message ${first}` : 'Save'
    const value = typed[field].trim()
    numberNote.textContent = ''
    if (!needNumber || !value || !('result' in p)) return
    // For an invoice their way never changes; only a landline is worth a word.
    if (invoice) { if (field === 'phone' && !textsReach(value)) numberNote.textContent = "That's a landline: it's kept for Call, but an invoice can't be texted to it."; return }
    if (field === 'email' && wayFor(c) !== 'email') numberNote.textContent = "Email becomes how they're messaged."
    else if (field === 'phone' && p.set.message_by !== undefined) numberNote.textContent = `${wayName(wayFor(result))} becomes how they're messaged.`
    // A landline is saved for Call; it cannot take a text or WhatsApp (AGREED.md section 8).
    else if (field === 'phone' && !reaches(result)) numberNote.textContent = textsReach(value) ? "It can't take their usual way of messaging, so it is kept for Call." : "That's a landline: it's kept for Call, but it can't take a text or WhatsApp."
  }
  paint()

  const waiting = (k: string | null): boolean => !!k && getQueue().some((q) => q.key === k && q.status !== 'conflict' && q.status !== 'failed')
  const busy = (on: boolean): void => { for (const b of buttons) b.disabled = on }
  // What a refusal left unsaved, in the owner's words, once each.
  const unsavedWords = (fields: string[], set: Record<string, unknown>): string => {
    const words = [...new Set(fields.map((k) => k === 'phone' ? 'their number' : k === 'email' ? 'their email' : k === 'address' || k === 'postcode' ? 'their address'
      : k === 'postcode_enough' ? postcodeWords('the postcode is enough') : k === 'message_by' ? (set.message_by === 'none' ? "Don't message" : "how they're messaged") : k))]
    return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0] ?? 'your change'
  }

  const go = async (choice?: Choice): Promise<void> => {
    addressError.textContent = ''
    const p = plan(choice)
    if ('error' in p) {
      if (p.at === 'number') numberError.textContent = p.error
      else if (p.at === 'address') addressError.textContent = p.error
      else s.error.textContent = p.error
      return
    }
    if ('choice' in p) {
      clear(choiceBox)
      const use = el('button', { type: 'button', class: 'btn-secondary' }, `Use ${p.choice.typed}`)
      const keep = el('button', { type: 'button', class: 'btn-secondary' }, `Keep ${p.choice.onFile}`)
      use.addEventListener('click', () => { postcodeChoice = 'typed'; void go(choice) })
      keep.addEventListener('click', () => { postcodeChoice = 'on_file'; void go(choice) })
      choiceBox.append(el('p', { class: 'add-details-choice-words' }, `You typed ${p.choice.typed}, and ${p.choice.onFile} is on file. Which is right?`), el('div', { class: 'add-details-choice-buttons' }, use, keep))
      return
    }
    // Only "Save and message" opens their message; neither choice does (F18, R14).
    const message = !choice && !!opts.message && reaches(p.result)
    busy(true)
    const path = `/api/customers/${c.id}`
    const version = c.version
    // The save's own key, read off the queue the moment mutate records it (nothing is awaited before), so a refused
    // attempt can be taken off the phone (G E06).
    const queued = new Set(getQueue().map((q) => q.key))
    const sending = mutate<Record<string, unknown>>({ label: `Details for ${name || 'a customer'}`, method: 'PATCH', path, body: { version, ...p.set, base: p.base } })
    const sent = getQueue().find((q) => !queued.has(q.key) && q.path === path)?.key ?? null
    const ack = await sending
    busy(false)
    if (ack.status === 404) {
      // Removed on another phone while the box was open: said plainly, and the refused save is not left red on the
      // phone (build-messages.md 4; it read "customers #9701 not found").
      if (sent) dismiss(sent)
      s.error.textContent = `${name || 'They'} ${name ? 'was' : 'were'} removed from your customers, so nothing was saved.`
      return
    }
    if (ack.status === 409) {
      // Changed on another phone first, in a box this one changes too (G E06): the refused attempt is dismissed, so it
      // is not left on the phone to send again by itself.
      if (sent) dismiss(sent)
      let fresh: DetailsCustomer | null = null
      try {
        fresh = (await api<{ customer: DetailsCustomer }>(path)).customer ?? null
        if (fresh) Object.assign(c, fresh)
      } catch { /* the words below still stand */ }
      // Everything this save asked for is already there (the same choice, or the same number, made on the other phone):
      // nothing is unsaved, so the box closes, with no Undo (nothing of this phone's landed). Otherwise what was typed
      // stays here, and what did not save is said (Astra's review F06, GPT-6.1's R06, 20 Oct 2026).
      const unsaved = Object.keys(p.set).filter((k) => !fresh || !sameValue(k, p.set[k], (fresh as unknown as Record<string, unknown>)[k]))
      if (!unsaved.length) {
        saved = true
        s.done()
        toast(choice === 'none' ? `${name || 'They'} ${name ? 'was' : 'were'} already set to Don't message on another phone.`
          : choice === 'enough' ? `Already done on another phone: ${postcodeWords('the postcode is enough')} for ${name || 'them'}.`
            : `${name ? `${name}'s` : 'Their'} details were already saved on another phone.`)
        opts.onSaved?.({ customer: c, waiting: false, message })
        return
      }
      const now = needNumber ? String(c[field] ?? '').trim() : ''
      const way = 'message_by' in p.set && fresh && !sameValue('message_by', p.set.message_by, fresh.message_by)
        ? (customerMessageBy(fresh.message_by) === 'none' ? " They're now set to Don't message." : ` They're now messaged by ${wayName(wayFor(fresh))}.`) : ''
      const typedHere = !!(needNumber && typed[field].trim()) || !!(needAddress && addressInput.value.trim() && key(addressInput.value) !== key(prefill))
      paint()
      s.error.textContent = `${name || 'They'} ${name ? 'was' : 'were'} changed on another phone just now.${now ? ` Now on file: ${now}.` : ''}${way} Not saved: ${unsavedWords(unsaved, p.set)}. ${typedHere ? 'What you typed is still here: check it and save again.' : 'Tap it again if that is still right.'}`
      return
    }
    if (ack.status === 422 && 'postcode' in p.set) {
      // A postcode another round's area keeps (functions/lib/enfield.ts), shown on the address, the number kept in its
      // box (Sonnet's edge cases, S E14).
      addressError.textContent = "That postcode is for another round's area, so it can't go on this round."
      const without = el('button', { type: 'button', class: 'btn-secondary add-details-without' }, 'Save without the postcode')
      without.addEventListener('click', () => { dropPostcode = true; without.remove(); void go(choice) })
      addressError.append(' ', without)
      return
    }
    if (!ack.ok && !waiting(sent)) {
      s.error.textContent = ack.error ?? "That didn't save. Try again."
      return
    }
    const row = ack.ok && ack.row ? ack.row : null
    Object.assign(c, p.set, row ?? { version: version + 1 })
    // Undo carries base, what this save made each box, for every box but the postcode (the same rule as the save), so
    // it still lands after another phone changed a different box, and is refused only if someone changed that same box
    // since (build-messages.md 4: it was refused after any change at all).
    const undoBase: Record<string, unknown> = {}
    for (const k of Object.keys(p.set)) if (k !== 'postcode') undoBase[k] = p.set[k] ?? null
    undoPatch({
      text: ack.ok ? p.text : `${p.text}. No signal: saved on this phone, it sends as soon as it can`,
      label: `Undo details for ${name || 'a customer'}`,
      path,
      ack,
      version,
      body: { ...p.before, base: undoBase },
      // The customer in hand goes back at the tap, as the save changed it at the tap: with no signal the ask comes back
      // straight away (Astra's review F12).
      before: () => { for (const k of Object.keys(p.set)) (c as unknown as Record<string, unknown>)[k] = p.before[k] ?? null },
      after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')),
    })
    saved = true
    s.done()
    opts.onSaved?.({ customer: c, waiting: !ack.ok, message })
  }
  save.addEventListener('click', () => void go())
  addressInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void go() } })

  // ---- or nothing to add (tidy-up 2 item 3, the drawing): each is Save with that choice added, saved at once ----
  const offer = (choice: Choice, words: string, help: string): void => {
    const b = el('button', { type: 'button', class: `btn-secondary add-details-instead add-details-${choice === 'none' ? 'no-messages' : 'postcode-enough'}` }, words) as HTMLButtonElement
    b.addEventListener('click', () => void go(choice))
    buttons.push(b)
    s.body.append(b, el('p', { class: 'muted field-help add-details-instead-help' }, help))
  }
  // Only with a postcode to be enough: with none, there is nothing to choose.
  const postcodeHere = needAddress && !!postcodeOf(c)
  // Not for an invoice: a commercial customer is asked until an invoice can reach them (item 12).
  const noMessagesHere = needNumber && !invoice && !forInvoices(c)
  if (noMessagesHere || postcodeHere) s.body.append(el('hr', { class: 'add-details-rule' }))
  if (noMessagesHere) offer('none', "They don't take messages", 'A business, say. Roundly stops asking, and you can change it on their page.')
  if (postcodeHere) offer('enough', postcodeWords('The postcode is enough'), 'Roundly stops asking for their street. You can change it on their page.')
  // On a computer the box is ready to type in; on a phone it is tapped once the sheet is up (sheet-keyboard.ts).
  cursorOnOpen(needNumber ? numberInput : addressInput)
}

// Their page's tick box (build-messages.md 3.4): the same choice as the box's button, saved straight away with Undo.
export async function setPostcodeEnough(c: DetailsCustomer, on: boolean, after: () => void): Promise<void> {
  const path = `/api/customers/${c.id}`
  const was = Number(withWaiting(c).postcode_enough) === 1 ? 1 : 0
  const to = on ? 1 : 0
  if (was === to) { after(); return }
  const name = c.name.trim()
  const version = c.version
  const queued = new Set(getQueue().map((q) => q.key))
  const sending = mutate<Record<string, unknown>>({ label: `Postcode for ${name || 'a customer'}`, method: 'PATCH', path, body: { version, postcode_enough: to, base: { postcode_enough: was } } })
  const sent = getQueue().find((q) => !queued.has(q.key) && q.path === path)?.key ?? null
  const ack = await sending
  if (!ack.ok && ack.status >= 400) {
    // Refused (changed on another phone first, or removed): said, not left on the phone, and the page reads again.
    if (sent) dismiss(sent)
    toast(ack.status === 404 ? `${name || 'They'} ${name ? 'was' : 'were'} removed from your customers, so nothing was saved.`
      : ack.status === 409 ? `${name || 'They'} ${name ? 'was' : 'were'} changed on another phone first. Nothing was saved: look again.` : ack.error ?? "That didn't save. Try again.")
    after()
    return
  }
  Object.assign(c, { postcode_enough: to }, ack.ok && ack.row ? ack.row : { version: version + 1 })
  const words = to ? `${name || 'They'}: ${postcodeWords("the postcode is enough. Roundly won't ask for their street")}` : `${name || 'They'}: Roundly asks for their street again`
  undoPatch({
    text: ack.ok ? words : `${words}. No signal: saved on this phone, it sends as soon as it can`,
    label: `Undo postcode for ${name || 'a customer'}`,
    path,
    ack,
    version,
    body: { postcode_enough: was, base: { postcode_enough: to } },
    before: () => { c.postcode_enough = was },
    after,
  })
  after()
}
